import { FC } from "react";

import { Box, chakra, Flex } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { useTranslation } from "next-i18next";

import Loader from "@components/Loader";
import SubmitInput from "@components/SubmitInput";
import responsiveStyle from "@lib/theme/responsiveStyle";

import { OrderFields } from "../../constants";
import useOrderFieldsLoading from "../../context/useOrderFieldsLoading";
import useLoadOffers from "../../hooks/common/useLoadOffers";
import useOrderCoupon from "../../hooks/useOrderCoupon";
import useOrderPrice from "../../hooks/useOrderPrice";

const OrderPrice: FC<{ withPromo?: boolean }> = ({ withPromo = true }) => {
  const { t } = useTranslation();

  const { price, oldPrice } = useOrderPrice();

  const { hasCoupon, isValid, promoCode, isBlocked } = useOrderCoupon();
  const { loadOffers } = useLoadOffers();

  const { isFieldLoading } = useOrderFieldsLoading();
  const isPriceLoading = isFieldLoading(OrderFields.OFFER_GROUP_ID);
  const isPromoCodeLoading = isFieldLoading(OrderFields.PROMO_CODE);

  const { setFieldValue } = useFormikContext();

  // If promo code is set as formik value, then it was correct
  // Calling loadOffers explicitly after promo code change

  const handleCodeSubmit = (code: string) => {
    const trimmedCode = code.trim();
    loadOffers(trimmedCode);
  };

  const handleCodeRemove = () => {
    setFieldValue(OrderFields.PROMO_CODE, undefined);
    loadOffers();
  };

  const isCouponSubmitted = hasCoupon && isValid && !isBlocked;
  const isCouponInvalid = hasCoupon && !isValid;
  const isRequestBlocked = hasCoupon && isBlocked;

  const getPromoErrorMessage = () => {
    if (isRequestBlocked) {
      return t("order-message-promo_code_request_limit");
    }
    if (isCouponInvalid) {
      return t("order-message-promo_code_invalid");
    }
    return undefined;
  };

  return (
    <Flex
      direction={responsiveStyle({ mobile: "column", desktop: "row-reverse" })}
      textAlign="right"
      justifyContent={responsiveStyle({ mobile: "flex-start", desktop: "space-between" })}
      alignItems={responsiveStyle({ mobile: "flex-end", desktop: "center" })}
    >
      <Loader isLoading={!!isPriceLoading}>
        <Box>
          {oldPrice && (
            <Box fontSize="0.875rem" color="red.lead" lineHeight="1" textDecoration="line-through">
              {oldPrice} {t("order-amount-currency")}
            </Box>
          )}
          <Box fontWeight={300} fontSize="1.125rem">
            {t("order-amount")}{" "}
            {price && (
              <chakra.span fontWeight={400} ml="1rem">
                {price} {t("order-amount-currency")}
              </chakra.span>
            )}
          </Box>
        </Box>
      </Loader>
      {withPromo && (
        <Box width="17.5rem" mt={responsiveStyle({ mobile: "1.5rem", desktop: "0" })}>
          <SubmitInput
            id="order-promo-code"
            submitLabel={t("order-promo_code-submit")}
            removeLabel={t("order-promo_code-remove")}
            label={t("order-promo_code-placeholder")}
            onSubmit={handleCodeSubmit}
            onRemove={handleCodeRemove}
            isSubmitted={isCouponSubmitted}
            isLoading={isPromoCodeLoading}
            isInvalid={isCouponInvalid}
            message={getPromoErrorMessage()}
            value={promoCode}
          />
        </Box>
      )}
    </Flex>
  );
};
export default OrderPrice;
